// File: frontend/src/pages/LedgerPage.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { feesAPI, usersAPI } from '../services/api';
import toast from 'react-hot-toast';
import Spinner from '../components/Spinner';

const STATUS_LABELS = {
    paid: { label: 'Paid', className: 'bg-emerald-100 text-emerald-800' },
    pending: { label: 'Pending', className: 'bg-amber-100 text-amber-800' },
    waived: { label: 'Waived', className: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400' }
};

const METHOD_LABELS = {
    cash: 'Cash',
    bank_transfer: 'Bank Transfer',
    line_pay: 'LINE Pay'
};

export default function LedgerPage() {
    const [teachers, setTeachers] = useState([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [teacherId, setTeacherId] = useState('');
    const [status, setStatus] = useState('');
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        usersAPI.getAll().then(allUsers => {
            setTeachers(allUsers.filter(u => u.role === 'teacher' || u.role === 'admin'));
        }).catch(() => {});
    }, []);

    const fetchLedger = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = {};
            if (fromDate) params.fromDate = fromDate;
            if (toDate) params.toDate = toDate;
            if (teacherId) params.teacherId = teacherId;
            if (status) params.status = status;
            const result = await feesAPI.getLedger(params);
            setData(result);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to load fee ledger.');
        } finally {
            setIsLoading(false);
        }
    }, [fromDate, toDate, teacherId, status]);

    useEffect(() => {
        fetchLedger();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const summary = data?.summary;
    const records = data?.records || [];

    return (
        <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-xs border border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-3">💰 Fee Ledger</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">From Date</label>
                        <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full border rounded p-2 text-sm" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">To Date</label>
                        <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full border rounded p-2 text-sm" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Teacher</label>
                        <select value={teacherId} onChange={e => setTeacherId(e.target.value)} className="w-full border rounded p-2 text-sm">
                            <option value="">All Teachers</option>
                            {teachers.map(t => <option key={t.id} value={t.id}>{t.username}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                        <select value={status} onChange={e => setStatus(e.target.value)} className="w-full border rounded p-2 text-sm">
                            <option value="">All</option>
                            <option value="paid">Paid</option>
                            <option value="pending">Pending</option>
                            <option value="waived">Waived</option>
                        </select>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={fetchLedger}
                    disabled={isLoading}
                    className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold px-4 py-2 rounded-lg text-sm shadow-xs transition-colors"
                >
                    {isLoading ? 'Loading...' : 'Apply Filters'}
                </button>
            </div>

            {summary && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <div className="bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xs">
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">Total Collected</div>
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">${summary.total_collected} NTD</div>
                    </div>
                    <div className="bg-amber-50 p-3 rounded-xl border border-amber-200 shadow-xs">
                        <div className="text-xs text-amber-700 font-medium">Cash</div>
                        <div className="text-lg font-bold text-amber-900">${summary.total_collected_cash} NTD</div>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-xl border border-blue-200 shadow-xs">
                        <div className="text-xs text-blue-700 font-medium">Bank Transfer</div>
                        <div className="text-lg font-bold text-blue-900">${summary.total_collected_bank_transfer} NTD</div>
                    </div>
                    <div className="bg-green-50 p-3 rounded-xl border border-green-200 shadow-xs">
                        <div className="text-xs text-green-700 font-medium">LINE Pay</div>
                        <div className="text-lg font-bold text-green-900">${summary.total_collected_line_pay} NTD</div>
                    </div>
                    <div className="bg-red-50 p-3 rounded-xl border border-red-200 shadow-xs">
                        <div className="text-xs text-red-700 font-medium">Pending</div>
                        <div className="text-lg font-bold text-red-900">${summary.total_pending} NTD</div>
                    </div>
                </div>
            )}

            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs overflow-x-auto">
                {isLoading ? (
                    <div className="p-8 flex justify-center"><Spinner /></div>
                ) : records.length === 0 ? (
                    <div className="p-8 text-center text-sm text-gray-400 dark:text-gray-500 italic">No fee records match these filters.</div>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-800/60">
                            <tr>
                                <th className="py-2.5 px-4 text-left font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase">Date</th>
                                <th className="py-2.5 px-4 text-left font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase">Student</th>
                                <th className="py-2.5 px-4 text-left font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase">Class</th>
                                <th className="py-2.5 px-4 text-left font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase">Teacher</th>
                                <th className="py-2.5 px-4 text-left font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase">Amount</th>
                                <th className="py-2.5 px-4 text-left font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase">Method</th>
                                <th className="py-2.5 px-4 text-left font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase">Status</th>
                                <th className="py-2.5 px-4 text-left font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase">Confirmed By</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {records.map(r => (
                                <tr key={r.id} className="hover:bg-gray-50/80">
                                    <td className="py-2.5 px-4 text-gray-700 dark:text-gray-300">{r.fee_date}</td>
                                    <td className="py-2.5 px-4 font-medium text-gray-900 dark:text-gray-100">{r.student_name}</td>
                                    <td className="py-2.5 px-4 text-gray-700 dark:text-gray-300">{r.class_name}</td>
                                    <td className="py-2.5 px-4 text-gray-500 dark:text-gray-400">{r.teacher_name || '—'}</td>
                                    <td className="py-2.5 px-4 font-bold text-gray-900 dark:text-gray-100">${r.amount} NTD</td>
                                    <td className="py-2.5 px-4 text-gray-600 dark:text-gray-400">{METHOD_LABELS[r.payment_method] || r.payment_method}</td>
                                    <td className="py-2.5 px-4">
                                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_LABELS[r.status]?.className || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}>
                                            {STATUS_LABELS[r.status]?.label || r.status}
                                        </span>
                                    </td>
                                    <td className="py-2.5 px-4 text-gray-500 dark:text-gray-400">{r.confirmed_by_name || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
